import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Flags from 'react-country-flag';
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import styles from '../../Pricing.module.css';

const CHARGEBACK_TOOLTIP = 'The user is protected from the unpredictability of disputes. If your business faces a fraudulent dispute, we will cover the disputed amount and waive any dispute fees — submission of evidence may be required. This added layer of security only applies to credit/debit card payments processed through Fyntiq. This feature is not available for payments using Discover card.';

const InfoTip = ({ title }) => (
  <Tooltip title={title} position="top" trigger="mouseenter" arrow>
    <i className={`fa-regular fa-circle-info ${styles.infoIcon}`} />
  </Tooltip>
);

const toOption = (c) => ({
  value: c.name,
  label: (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <Flags className="me-2" countryCode={c.alpha2Code} svg style={{ marginRight: 8 }} />
      <span>{c.name}</span>
    </span>
  ),
  countryData: c,
});

const Fees = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [country, setCountry] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/utility/public/all-country-price`)
      .then((response) => response.json())
      .then((data) => setCountries(data.data.countryPrice))
      .catch((error) => console.error('Error fetching country data:', error));
  }, []);

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await fetch('https://api.country.is/');
        const data = await response.json();
        setCountry(data.country);
      } catch (error) {
        console.error('Error fetching country:', error);
      }
    };
    fetchCountry();
  }, []);

  const options = countries.map(toOption);

  useEffect(() => {
    if (countries.length === 0) return;
    const matched = country
      ? countries.find((c) => c.sortname === country)
      : countries[0];
    setSelectedCountry(toOption(matched || countries[0]));
  }, [countries, country]);

  const renderDomesticFees = (items) => {
    if (!items?.length) return null;
    return (
      <>
        {items[1]?.fee && (
          <div className={styles.feesFeeItem}>
            <p className={styles.feesFee}>{items[1].fee}</p>
            <p className={styles.feesDesc}>
              Credit &amp; Debit Cards with Chargeback Insurance* <InfoTip title={CHARGEBACK_TOOLTIP} />
            </p>
          </div>
        )}
        {items[0]?.fee && (
          <div className={styles.feesFeeItem}>
            <p className={styles.feesFee}>{items[0].fee}</p>
            <p className={styles.feesDesc}>Bank (ACH) Transfer <InfoTip title={CHARGEBACK_TOOLTIP} /></p>
          </div>
        )}
      </>
    );
  };

  const renderInternationalFees = (items) => {
    if (!items?.length) return null;
    return (
      <>
        {items[0]?.fee && (
          <div className={styles.feesFeeItem}>
            <p className={styles.feesFee}>{items[0].fee}</p>
            <p className={styles.feesDesc}>
              Credit &amp; Debit Cards with Chargeback Insurance* <InfoTip title={CHARGEBACK_TOOLTIP} />
            </p>
          </div>
        )}
        {items[1]?.fee && (
          <div className={styles.feesFeeItem}>
            <p className={styles.feesFee}>{items[1].fee}</p>
            <p className={styles.feesDesc}>Bank (ACH) Transfer <InfoTip title={CHARGEBACK_TOOLTIP} /></p>
          </div>
        )}
      </>
    );
  };

  return (
    <section className={styles.sec} id="fees" style={{ paddingTop: 0 }}>
      <div className={styles.container}>
        <div className={`${styles.secHead} ${styles.secHeadCenter} ${styles.sr}`} data-sr>
          <span className={styles.eyebrow}><span className={styles.dot} />Processing fees</span>
          <h2>The right plan for your business.</h2>
          <p className={styles.secLead}>Transparent per-transaction pricing. Select your country to see domestic and international rates.</p>
        </div>

        <div className={`${styles.countrySelect} ${styles.sr}`} data-sr>
          <Select
            options={options}
            value={selectedCountry}
            onChange={setSelectedCountry}
            placeholder="Select a country"
          />
        </div>

        <div className={`${styles.feesCard} ${styles.sr}`} data-sr>
          <div className={styles.feesCardInner}>
            <h3 className={styles.feesCardTitle}>
              Domestic &amp; International
              <small>Pass-fee enabled</small>
            </h3>
            <div className={styles.feesHighlight}>
              <strong>0%</strong>
            </div>
            <p className={styles.feesDesc}>
              Credit &amp; Debit Cards with<br />Chargeback Insurance <InfoTip title={CHARGEBACK_TOOLTIP} />
            </p>
          </div>

          <div className={styles.feesDivider}>OR</div>

          <div className={styles.feesCardInner}>
            <div className={styles.feesGrid}>
              <div>
                <h4 className={styles.feesColTitle}>
                  <span className={styles.feesBadge}>Domestic</span>
                  <small style={{ fontWeight: 500, color: 'var(--ink-soft)' }}>Pass-fee disabled</small>
                </h4>
                {selectedCountry?.countryData && renderDomesticFees(selectedCountry.countryData.public.domestic)}
              </div>
              <div className={styles.feesVline} />
              <div>
                <h4 className={styles.feesColTitle}>
                  <span className={styles.feesBadge}>International</span>
                  <small style={{ fontWeight: 500, color: 'var(--ink-soft)' }}>Pass-fee disabled</small>
                </h4>
                {selectedCountry?.countryData && renderInternationalFees(selectedCountry.countryData.public.international)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Fees;
